import { useEffect } from "react";
import focusableSelectors from "focusable-selectors";

const DialogScript = () => {
	useEffect(() => {
		if (typeof window === "undefined" || typeof document === "undefined") {
			return undefined;
		}

		const win = window;

		win.__pdDialogScriptMounts = (win.__pdDialogScriptMounts || 0) + 1;

		if (win.__pdDialogScriptInitialized) {
			return () => {
				win.__pdDialogScriptMounts -= 1;

				if (win.__pdDialogScriptMounts <= 0 && win.__pdDialogScriptCleanup) {
					win.__pdDialogScriptCleanup();
				}
			};
		}

		win.__pdDialogScriptInitialized = true;

		const TAB_KEY = "Tab";
		const ESCAPE_KEY = "Escape";

		const state = {
			activeDialog: null,
			previouslyFocused: null,
			scrollY: 0,
			bodyStyle: null,
			htmlStyle: null,
		};

		function toArray(collection) {
			return Array.prototype.slice.call(collection);
		}

		function getDialogById(id) {
			if (!id) return null;

			return document.getElementById(id) || document.querySelector(`[data-a11y-dialog="${id}"]`);
		}

		function getFocusableChildren(node) {
			return toArray(node.querySelectorAll(focusableSelectors.join(","))).filter((child) => {
				const isVisible = !!(
					child.offsetWidth ||
					child.offsetHeight ||
					child.getClientRects().length
				);

				const isDisabled =
					child.hasAttribute("disabled") || child.getAttribute("aria-hidden") === "true";

				return isVisible && !isDisabled;
			});
		}

		function prepareDialogs() {
			const dialogs = document.querySelectorAll("[data-a11y-dialog], .modal[id]");

			dialogs.forEach((dialog) => {
				if (!dialog.hasAttribute("role")) {
					dialog.setAttribute("role", "dialog");
				}

				dialog.setAttribute("aria-modal", "true");

				if (!dialog.hasAttribute("aria-hidden")) {
					dialog.setAttribute("aria-hidden", "true");
				}

				if (!dialog.hasAttribute("tabindex")) {
					dialog.setAttribute("tabindex", "-1");
				}
			});
		}

		function lockScroll() {
			const body = document.body;
			const html = document.documentElement;

			if (body.dataset.pdModalScrollLocked === "true") {
				return;
			}

			state.scrollY = window.scrollY || html.scrollTop || 0;

			state.bodyStyle = {
				position: body.style.position,
				top: body.style.top,
				left: body.style.left,
				right: body.style.right,
				width: body.style.width,
				overflow: body.style.overflow,
				touchAction: body.style.touchAction,
			};

			state.htmlStyle = {
				overflow: html.style.overflow,
			};

			body.dataset.pdModalScrollLocked = "true";

			body.style.position = "fixed";
			body.style.top = `-${state.scrollY}px`;
			body.style.left = "0";
			body.style.right = "0";
			body.style.width = "100%";
			body.style.overflow = "hidden";
			body.style.touchAction = "none";

			html.style.overflow = "hidden";
		}

		function unlockScroll() {
			const body = document.body;
			const html = document.documentElement;

			if (body.dataset.pdModalScrollLocked !== "true") {
				return;
			}

			delete body.dataset.pdModalScrollLocked;

			if (state.bodyStyle) {
				body.style.position = state.bodyStyle.position;
				body.style.top = state.bodyStyle.top;
				body.style.left = state.bodyStyle.left;
				body.style.right = state.bodyStyle.right;
				body.style.width = state.bodyStyle.width;
				body.style.overflow = state.bodyStyle.overflow;
				body.style.touchAction = state.bodyStyle.touchAction;
			} else {
				body.style.position = "";
				body.style.top = "";
				body.style.left = "";
				body.style.right = "";
				body.style.width = "";
				body.style.overflow = "";
				body.style.touchAction = "";
			}

			if (state.htmlStyle) {
				html.style.overflow = state.htmlStyle.overflow;
			} else {
				html.style.overflow = "";
			}

			window.scrollTo(0, state.scrollY || 0);
		}

		function focusFirstItem(dialog) {
			window.requestAnimationFrame(() => {
				const focusableChildren = getFocusableChildren(dialog);
				const target = dialog.querySelector("[autofocus]") || focusableChildren[0] || dialog;

				if (target && typeof target.focus === "function") {
					try {
						target.focus({ preventScroll: true });
					} catch (error) {
						target.focus();
					}
				}
			});
		}

		function openDialog(id, opener) {
			const dialog = getDialogById(id);

			if (!dialog) {
				return;
			}

			prepareDialogs();

			if (state.activeDialog && state.activeDialog !== dialog) {
				closeDialog();
			}

			state.activeDialog = dialog;
			state.previouslyFocused = opener || document.activeElement;

			dialog.setAttribute("aria-hidden", "false");
			dialog.classList.add("is-open");

			lockScroll();
			focusFirstItem(dialog);
		}

		function closeDialog() {
			const dialog = state.activeDialog;

			if (!dialog) {
				unlockScroll();
				return;
			}

			state.activeDialog = null;

			dialog.setAttribute("aria-hidden", "true");
			dialog.classList.remove("is-open");

			unlockScroll();

			const previous = state.previouslyFocused;
			state.previouslyFocused = null;

			window.requestAnimationFrame(() => {
				if (previous && document.contains(previous) && typeof previous.focus === "function") {
					try {
						previous.focus({ preventScroll: true });
					} catch (error) {
						previous.focus();
					}
				}
			});
		}

		function trapTabKey(event) {
			const dialog = state.activeDialog;

			if (!dialog) return;

			const focusableChildren = getFocusableChildren(dialog);

			if (!focusableChildren.length) {
				event.preventDefault();
				dialog.focus();
				return;
			}

			const firstItem = focusableChildren[0];
			const lastItem = focusableChildren[focusableChildren.length - 1];

			if (event.shiftKey && document.activeElement === firstItem) {
				event.preventDefault();
				lastItem.focus();
				return;
			}

			if (!event.shiftKey && document.activeElement === lastItem) {
				event.preventDefault();
				firstItem.focus();
			}
		}

		function handleDialogInteraction(event) {
			const opener = event.target.closest("[data-a11y-dialog-show]");

			if (opener) {
				const id = opener.getAttribute("data-a11y-dialog-show");

				event.preventDefault();
				event.stopPropagation();

				openDialog(id, opener);
				return;
			}

			const closer = event.target.closest("[data-a11y-dialog-hide]");

			if (closer) {
				const closeTarget = closer.getAttribute("data-a11y-dialog-hide");

				if (!closeTarget || (state.activeDialog && state.activeDialog.id === closeTarget)) {
					event.preventDefault();
					event.stopPropagation();

					closeDialog();
				}
			}
		}

		function handleDocumentClick(event) {
			handleDialogInteraction(event);
		}

		function handleKeyDown(event) {
			if (!state.activeDialog) return;

			if (event.key === ESCAPE_KEY) {
				event.preventDefault();
				closeDialog();
				return;
			}

			if (event.key === TAB_KEY) {
				trapTabKey(event);
			}
		}

		function handleFocusIn(event) {
			const dialog = state.activeDialog;

			if (!dialog) return;

			if (!dialog.contains(event.target)) {
				focusFirstItem(dialog);
			}
		}

		prepareDialogs();

		document.addEventListener("click", handleDocumentClick);
		document.addEventListener("keydown", handleKeyDown);
		document.addEventListener("focusin", handleFocusIn);

		const cleanup = () => {
			closeDialog();

			document.removeEventListener("click", handleDocumentClick);
			document.removeEventListener("keydown", handleKeyDown);
			document.removeEventListener("focusin", handleFocusIn);

			delete win.__pdDialogScriptInitialized;
			delete win.__pdDialogScriptCleanup;
			delete win.__pdDialogScriptMounts;
		};

		win.__pdDialogScriptCleanup = cleanup;

		return () => {
			win.__pdDialogScriptMounts -= 1;

			if (win.__pdDialogScriptMounts <= 0) {
				cleanup();
			}
		};
	}, []);

	return null;
};

export default DialogScript;
